import React, { useState, useMemo } from 'react';
import ShowcasePageWrapper from '../../showcase/shared/ShowcasePageWrapper';
import {
  SLDSLineChart,
  SLDSDoughnutChart,
  MiniDonut,
  SLDS_CHART_COLORS,
  SLDS_CHART_COLORS_ALPHA,
} from '../../showcase/shared/ChartComponents';
import Button from '@salesforce/design-system-react/components/button';
import Icon from '@salesforce/design-system-react/components/icon';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import ExpandableSection from '@salesforce/design-system-react/components/expandable-section';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------
const COLORS = {
  brand: '#0176d3',
  success: '#2e844a',
  warning: '#fe9339',
  error: '#c23934',
  border: '#dddbda',
  bg: '#f3f3f3',
  textPrimary: '#080707',
  textSecondary: '#706e6b',
  white: '#ffffff',
  purple: '#9050e9',
  teal: '#06a59a',
};

const cardStyle = {
  backgroundColor: COLORS.white,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '4px',
  padding: '20px',
  marginBottom: '16px',
};

const fieldLabelStyle = {
  fontSize: '12px',
  color: COLORS.textSecondary,
  fontWeight: '400',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const fieldValueStyle = {
  fontSize: '14px',
  color: COLORS.textPrimary,
  fontWeight: '400',
  marginBottom: '16px',
};

const Badge = ({ label, color, bgColor }) => (
  <span style={{
    display: 'inline-block', backgroundColor: bgColor, color: color,
    padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', lineHeight: '20px',
  }}>
    {label}
  </span>
);

const FieldDisplay = ({ label, value, isLink }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={{
      ...fieldValueStyle,
      color: isLink ? COLORS.brand : COLORS.textPrimary,
      cursor: isLink ? 'pointer' : 'default',
    }}>
      {value}
    </div>
  </div>
);

const TimelineItem = ({ icon, iconColor, iconBg, title, description, timestamp, isLast }) => (
  <div style={{ display: 'flex', gap: '12px', position: 'relative' }}>
    {!isLast && (
      <div style={{ position: 'absolute', left: '15px', top: '32px', bottom: '-12px', width: '2px', backgroundColor: '#dddbda' }} />
    )}
    <div style={{
      width: '32px', height: '32px', borderRadius: '50%', backgroundColor: iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1,
    }}>
      <Icon category="utility" name={icon} size="x-small" style={{ fill: iconColor }} />
    </div>
    <div style={{ flex: 1, paddingBottom: isLast ? '0' : '20px' }}>
      <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.textPrimary, marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#3e3e3c', marginBottom: '4px' }}>{description}</div>
      <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{timestamp}</div>
    </div>
  </div>
);

const QuickActionButton = ({ icon, label }) => (
  <button style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
    padding: '12px 8px', backgroundColor: 'transparent', border: `1px solid ${COLORS.border}`,
    borderRadius: '4px', cursor: 'pointer', width: '100%',
  }}>
    <Icon category="utility" name={icon} size="small" style={{ fill: COLORS.brand }} />
    <span style={{ fontSize: '11px', color: '#3e3e3c', fontWeight: '500', textAlign: 'center', lineHeight: '1.3' }}>{label}</span>
  </button>
);

// ---------------------------------------------------------------------------
// Custom DataTable Cells
// ---------------------------------------------------------------------------
const DonationNameCell = ({ children, ...props }) => (
  <DataTableCell {...props}>
    <span style={{ color: COLORS.brand, cursor: 'pointer', fontWeight: '500' }}>{children}</span>
  </DataTableCell>
);
DonationNameCell.displayName = DataTableCell.displayName;

const DonationAmountCell = ({ children, ...props }) => {
  const amount = props.item && props.item.amount;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
        ${typeof amount === 'number' ? amount.toLocaleString('en-US') : amount}
      </span>
    </DataTableCell>
  );
};
DonationAmountCell.displayName = DataTableCell.displayName;

const DonationStatusCell = ({ children, ...props }) => {
  const status = props.item && props.item.status;
  const statusColors = {
    'Active': { bg: '#e8f5e9', color: '#2e844a' },
    'Completed': { bg: '#eef4ff', color: '#0176d3' },
    'Posted': { bg: '#e8f5e9', color: '#2e844a' },
  };
  const colorSet = statusColors[status] || { bg: '#f3f3f3', color: '#706e6b' };
  return (
    <DataTableCell {...props}>
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
        fontWeight: '600', backgroundColor: colorSet.bg, color: colorSet.color,
      }}>
        {status}
      </span>
    </DataTableCell>
  );
};
DonationStatusCell.displayName = DataTableCell.displayName;

// ---------------------------------------------------------------------------
// Navigation Bar
// ---------------------------------------------------------------------------
const NavBar = ({ activeTab, onTabChange }) => {
  const tabs = ['Record Home', 'Details', 'Related', 'Activity'];
  return (
    <div style={{
      backgroundColor: 'white', border: `1px solid ${COLORS.border}`, borderRadius: '4px',
      marginBottom: '16px', display: 'flex', alignItems: 'center',
    }}>
      {tabs.map((tab) => (
        <button key={tab} onClick={() => onTabChange(tab)} style={{
          padding: '12px 24px', fontSize: '14px',
          fontWeight: activeTab === tab ? '700' : '400',
          color: activeTab === tab ? COLORS.brand : '#3e3e3c',
          backgroundColor: 'transparent', border: 'none',
          borderBottom: activeTab === tab ? `3px solid ${COLORS.brand}` : '3px solid transparent',
          cursor: 'pointer',
        }}>
          {tab}
        </button>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const NPSPContact = () => {
  const [activeNav, setActiveNav] = useState('Record Home');
  const [relatedTab, setRelatedTab] = useState(0);
  const [timelineExpanded, setTimelineExpanded] = useState(true);

  // Charts
  const annualTrendData = useMemo(() => ({
    labels: ['2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'],
    datasets: [{
      label: 'Annual Giving',
      data: [2500, 5000, 7500, 8000, 12000, 15000, 18000, 14500, 22000, 18500],
      borderColor: SLDS_CHART_COLORS[0],
      backgroundColor: SLDS_CHART_COLORS_ALPHA[0],
      fill: true, tension: 0.35, pointRadius: 3,
      pointBackgroundColor: SLDS_CHART_COLORS[0], borderWidth: 2,
    }],
  }), []);

  const annualTrendOptions = useMemo(() => ({
    plugins: { legend: { display: false } },
    scales: { y: { ticks: { callback: (v) => `$${(v / 1000).toFixed(0)}K` } } },
  }), []);

  const designationData = useMemo(() => ({
    labels: ['General', 'Capital', 'Scholarship', 'Program', 'Emergency'],
    datasets: [{
      data: [42000, 35000, 22000, 16000, 10000],
      backgroundColor: [SLDS_CHART_COLORS[0], SLDS_CHART_COLORS[1], SLDS_CHART_COLORS[2], SLDS_CHART_COLORS[4], SLDS_CHART_COLORS[3]],
      borderColor: COLORS.white, borderWidth: 3,
    }],
  }), []);

  const designationOptions = useMemo(() => ({
    cutout: '55%',
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 11 }, padding: 10, boxWidth: 12 } },
    },
  }), []);

  // Data
  const donations = useMemo(() => [
    { id: 'd-1', name: 'Year-End Major Gift 2025', amount: 25000, date: '2025-12-28', campaign: 'Year-End Appeal 2025', status: 'Posted' },
    { id: 'd-2', name: 'Fall Fundraiser Gift', amount: 10000, date: '2025-09-15', campaign: 'Fall Fundraiser', status: 'Posted' },
    { id: 'd-3', name: 'Spring Gala Gift', amount: 5000, date: '2025-06-01', campaign: 'Spring Gala 2025', status: 'Posted' },
    { id: 'd-4', name: 'Monthly Gift - December', amount: 500, date: '2025-12-15', campaign: 'Monthly Giving', status: 'Posted' },
    { id: 'd-5', name: 'Year-End Gift 2024', amount: 22000, date: '2024-12-20', campaign: 'Year-End Appeal 2024', status: 'Completed' },
    { id: 'd-6', name: 'Annual Fund Gift', amount: 15000, date: '2024-09-01', campaign: 'Annual Fund', status: 'Completed' },
  ], []);

  const recurringDonations = useMemo(() => [
    { id: 'r-1', name: 'Monthly General Fund', amount: 500, frequency: 'Monthly', status: 'Active', nextDate: 'Jan 15, 2026' },
    { id: 'r-2', name: 'Quarterly Scholarship', amount: 1250, frequency: 'Quarterly', status: 'Active', nextDate: 'Mar 1, 2026' },
  ], []);

  const relationships = useMemo(() => [
    { id: 'rel-1', name: 'Thomas Morrison', relationship: 'Spouse', type: 'Household' },
    { id: 'rel-2', name: 'Emily Morrison', relationship: 'Daughter', type: 'Household' },
    { id: 'rel-3', name: 'James Morrison', relationship: 'Son', type: 'Household' },
    { id: 'rel-4', name: 'Morrison Law Group', relationship: 'Employer', type: 'Organization' },
    { id: 'rel-5', name: 'Greater Bay Area Food Bank', relationship: 'Board Member', type: 'Organization' },
  ], []);

  const activities = useMemo(() => [
    { icon: 'call', iconColor: COLORS.success, iconBg: '#e8f5e9', title: 'Call: Year-End Giving Discussion', description: 'Confirmed $25K gift and mentioned interest in planned giving.', timestamp: 'Today at 10:30 AM' },
    { icon: 'email', iconColor: COLORS.brand, iconBg: '#e6f2fb', title: 'Email: Thank You - Holiday Meal Program', description: 'Sent personalized impact report for Holiday Meal Program.', timestamp: 'Dec 22, 2025 at 3:00 PM' },
    { icon: 'event', iconColor: COLORS.purple, iconBg: '#f0e6fb', title: 'Event: Annual Gala Dinner', description: 'Attended as VIP. Seated at Board Table. Matching gift announcement.', timestamp: 'Nov 15, 2025 at 6:00 PM' },
    { icon: 'task', iconColor: COLORS.warning, iconBg: '#fef3e8', title: 'Task: Gift Acknowledgment Sent', description: 'Mailed handwritten letter for $10K stock transfer.', timestamp: 'Oct 1, 2025 at 9:00 AM' },
    { icon: 'note', iconColor: COLORS.textSecondary, iconBg: COLORS.bg, title: 'Note: Planned Giving Prospect', description: 'Interest in charitable remainder trust. Connect with planned giving officer.', timestamp: 'Sep 1, 2025 at 2:00 PM' },
  ], []);

  return (
    <ShowcasePageWrapper
      title="Margaret Morrison"
      description="Donor contact record page"
      icon="contact"
      iconCategory="standard"
    >
      {/* Record Header */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#E27B7B',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '20px', fontWeight: '700', color: COLORS.white,
            }}>
              MM
            </div>
            <div>
              <div style={{ fontSize: '12px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Contact
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary, margin: 0 }}>
                  Margaret Morrison
                </h1>
                <Badge label="Major Donor" color={COLORS.white} bgColor={COLORS.brand} />
                <Badge label="Platinum Circle" color={COLORS.white} bgColor={COLORS.purple} />
              </div>
              <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>
                Managing Partner at <span style={{ color: COLORS.brand, cursor: 'pointer' }}>Morrison Law Group</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <Button label="Edit" variant="neutral" />
            <Button label="Clone" variant="neutral" />
            <Button label="Log Activity" variant="neutral" iconCategory="utility" iconName="log_a_call" iconPosition="left" />
            <Button label="New Donation" variant="brand" iconCategory="utility" iconName="add" iconPosition="left" />
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <NavBar activeTab={activeNav} onTabChange={setActiveNav} />

      {/* Three-Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '25% 50% 25%', gap: '16px', alignItems: 'start' }}>

        {/* LEFT COLUMN — Highlights */}
        <div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>
              Giving Summary
            </h3>
            {[
              { label: 'Lifetime Giving', value: '$125,000', color: COLORS.success },
              { label: 'This Year', value: '$18,500' },
              { label: 'Largest Gift', value: '$25,000' },
              { label: 'Total Gifts', value: '24' },
              { label: 'Average Gift', value: '$5,208' },
              { label: 'Consecutive Years', value: '10' },
              { label: 'Last Gift Date', value: 'Dec 28, 2025' },
              { label: 'Donor Since', value: 'Jan 2016' },
            ].map(m => (
              <div key={m.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', paddingBottom: '10px', borderBottom: `1px solid ${COLORS.bg}` }}>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{m.label}</span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: m.color || COLORS.textPrimary }}>{m.value}</span>
              </div>
            ))}

            {/* Recurring summary */}
            <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#eef4ff', borderRadius: '4px', border: `1px solid ${COLORS.brand}22` }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.brand, marginBottom: '6px' }}>Active Recurring</div>
              <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>
                2 active &middot; $500/mo + $1,250/qtr
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: COLORS.brand, marginTop: '4px' }}>$11,000/yr</div>
            </div>
          </div>

          {/* Wealth Indicators */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Wealth Indicators</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <MiniDonut value={92} total={100} color={COLORS.brand} size={48} />
              <div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.brand }}>92</div>
                <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>Propensity Score</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {[
                { label: 'DS Rating', value: 'A+' },
                { label: 'Capacity', value: '$2.5M' },
                { label: 'Real Estate', value: '$1.8M' },
                { label: 'Political', value: '$45K' },
                { label: 'Board Seats', value: '3' },
                { label: 'Foundations', value: '1' },
              ].map(i => (
                <div key={i.label} style={{ padding: '8px', backgroundColor: COLORS.bg, borderRadius: '4px', textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{i.label}</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: COLORS.textPrimary }}>{i.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CENTER COLUMN — Detail + Related */}
        <div>
          {/* Contact Information */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>
              Contact Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <FieldDisplay label="First Name" value="Margaret" />
              <FieldDisplay label="Last Name" value="Morrison" />
              <FieldDisplay label="Title" value="Managing Partner" />
              <FieldDisplay label="Account Name" value="Morrison Law Group" isLink />
              <FieldDisplay label="Email" value="margaret.morrison@morrisonlaw.com" isLink />
              <FieldDisplay label="Phone" value="(415) 555-9876" />
              <FieldDisplay label="Mobile" value="(415) 555-9877" />
              <FieldDisplay label="Preferred Communication" value="Email" />
              <FieldDisplay label="Household" value="Morrison Household" isLink />
              <FieldDisplay label="Donor Level" value="Platinum Circle" />
              <div style={{ marginBottom: '16px' }}>
                <div style={fieldLabelStyle}>Contact Owner</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', backgroundColor: COLORS.brand,
                    color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', fontWeight: '700',
                  }}>JA</div>
                  <span style={{ fontSize: '14px', color: COLORS.brand, cursor: 'pointer' }}>Jennifer Adams</span>
                </div>
              </div>
              <FieldDisplay label="Do Not Call" value="No" />
              <div style={{ gridColumn: '1 / -1' }}>
                <FieldDisplay label="Mailing Address" value="456 Presidio Avenue, San Francisco, CA 94115" />
              </div>
            </div>
          </div>

          {/* Related Lists */}
          <div style={cardStyle}>
            <Tabs id="contact-related-tabs" selectedIndex={relatedTab} onSelect={(index) => setRelatedTab(index)}>
              <TabsPanel label={`Donations (${donations.length})`}>
                <div style={{ marginTop: '8px' }}>
                  <DataTable items={donations} id="contact-donations-table" fixedLayout>
                    <DataTableColumn label="Name" property="name" width="220px">
                      <DonationNameCell />
                    </DataTableColumn>
                    <DataTableColumn label="Amount" property="amount" width="110px">
                      <DonationAmountCell />
                    </DataTableColumn>
                    <DataTableColumn label="Date" property="date" width="110px" />
                    <DataTableColumn label="Campaign" property="campaign" width="170px" />
                    <DataTableColumn label="Status" property="status" width="90px">
                      <DonationStatusCell />
                    </DataTableColumn>
                  </DataTable>
                  <div style={{ textAlign: 'center', marginTop: '12px' }}>
                    <Button label="View All Donations" variant="base" />
                  </div>
                </div>
              </TabsPanel>
              <TabsPanel label="Recurring (2)">
                <div style={{ marginTop: '8px' }}>
                  {recurringDonations.map((rd, idx) => (
                    <div key={rd.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '14px', backgroundColor: COLORS.bg, borderRadius: '4px',
                      border: `1px solid ${COLORS.border}`, marginBottom: idx < recurringDonations.length - 1 ? '8px' : '0',
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.brand, cursor: 'pointer' }}>{rd.name}</div>
                        <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '4px' }}>
                          ${rd.amount.toLocaleString()} / {rd.frequency} &middot; Next: {rd.nextDate}
                        </div>
                      </div>
                      <Badge label={rd.status} color={COLORS.success} bgColor="#e8f5e9" />
                    </div>
                  ))}
                </div>
              </TabsPanel>
              <TabsPanel label={`Relationships (${relationships.length})`}>
                <div style={{ marginTop: '8px' }}>
                  {relationships.map((rel, idx) => (
                    <div key={rel.id} style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 0', borderBottom: idx < relationships.length - 1 ? `1px solid ${COLORS.bg}` : 'none',
                    }}>
                      <div style={{
                        width: '32px', height: '32px', borderRadius: rel.type === 'Organization' ? '6px' : '50%',
                        backgroundColor: rel.type === 'Organization' ? '#7F8DE1' : SLDS_CHART_COLORS[idx % SLDS_CHART_COLORS.length],
                        color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: '700', flexShrink: 0,
                      }}>
                        {rel.type === 'Organization'
                          ? <Icon category="standard" name="account" size="x-small" style={{ fill: 'white' }} />
                          : rel.name.split(' ').map(n => n[0]).join('')
                        }
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: COLORS.brand, cursor: 'pointer' }}>{rel.name}</div>
                        <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{rel.relationship}</div>
                      </div>
                      <Badge
                        label={rel.type}
                        color={rel.type === 'Organization' ? COLORS.brand : COLORS.purple}
                        bgColor={rel.type === 'Organization' ? '#eef4ff' : '#f0e6fb'}
                      />
                    </div>
                  ))}
                </div>
              </TabsPanel>
            </Tabs>
          </div>

          {/* Activity Timeline */}
          <div style={cardStyle}>
            <ExpandableSection title="Activity Timeline" isOpen={timelineExpanded} onToggleOpen={() => setTimelineExpanded(!timelineExpanded)}>
              <div style={{ padding: '8px 0' }}>
                {activities.map((a, idx) => (
                  <TimelineItem key={idx} icon={a.icon} iconColor={a.iconColor} iconBg={a.iconBg}
                    title={a.title} description={a.description} timestamp={a.timestamp} isLast={idx === activities.length - 1} />
                ))}
              </div>
            </ExpandableSection>
            <div style={{ textAlign: 'center', marginTop: '8px', borderTop: `1px solid ${COLORS.border}`, paddingTop: '12px' }}>
              <Button label="View All Activity" variant="base" />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN — Sidebar */}
        <div>
          {/* Quick Actions */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Quick Actions</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
              <QuickActionButton icon="call" label="Log a Call" />
              <QuickActionButton icon="email" label="Send Email" />
              <QuickActionButton icon="task" label="New Task" />
              <QuickActionButton icon="event" label="New Event" />
              <QuickActionButton icon="note" label="New Note" />
              <QuickActionButton icon="opportunity" label="New Donation" />
            </div>
          </div>

          {/* Annual Giving Trend */}
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
              Annual Giving Trend
            </div>
            <SLDSLineChart data={annualTrendData} options={annualTrendOptions} height={150} />
          </div>

          {/* Designation Mix */}
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
              Giving by Designation
            </div>
            <SLDSDoughnutChart data={designationData} options={designationOptions} height={180} />
          </div>

          {/* Soft Credits */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Soft Credits</h3>
            {[
              { from: 'Morrison Law Group', amount: '$25,000', type: 'Corporate Match' },
              { from: 'Thomas Morrison', amount: '$10,000', type: 'Household' },
              { from: 'Morrison Family Foundation', amount: '$15,000', type: 'Foundation' },
            ].map((c, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: idx < 2 ? `1px solid ${COLORS.bg}` : 'none' }}>
                <div>
                  <div style={{ fontSize: '13px', color: COLORS.brand, cursor: 'pointer' }}>{c.from}</div>
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>{c.type}</div>
                </div>
                <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>{c.amount}</span>
              </div>
            ))}
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>Total</span>
              <span style={{ fontSize: '13px', fontWeight: '700', color: COLORS.textPrimary }}>$50,000</span>
            </div>
          </div>
        </div>
      </div>
    </ShowcasePageWrapper>
  );
};

export default NPSPContact;
